import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Download, Droplets } from "lucide-react";
import { useContent, useContentReady } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";
import { slugify } from "@/lib/slug";

const ease = [0.22, 1, 0.36, 1];

export default function ModelPage() {
  const { id, slug } = useParams();
  const content = useContent();
  const ready = useContentReady();
  const { t } = useLang();
  const p = content.products.find((x) => x.id === id);

  if (!ready) {
    return (
      <section
        data-testid="model-detail-loading"
        className="mx-auto flex max-w-[1440px] items-center justify-center px-6 pb-28 pt-32 lg:px-12 lg:pt-40"
      >
        <p className="text-sm uppercase tracking-[0.2em] text-sage">Loading…</p>
      </section>
    );
  }
  if (!p) return <Navigate to="/products" replace />;
  const models = p.models || [];
  const idx = models.findIndex((m) => slugify(m.name) === slug);
  const m = models[idx];
  if (!m) return <Navigate to={`/products/${p.id}`} replace />;

  const prev = models[idx - 1];
  const next = models[idx + 1];
  const others = models.filter((_, i) => i !== idx).slice(0, 5);
  const specs = m.specs && m.specs.length ? m.specs : p.specs || [];

  return (
    <section
      data-testid="model-detail"
      className="mx-auto max-w-[1440px] px-6 pb-28 pt-32 lg:px-12 lg:pt-40"
    >
      <Link
        to={`/products/${p.id}`}
        data-testid="back-to-category-link"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sage transition-colors duration-300 hover:text-harvest"
      >
        <ArrowLeft className="h-4 w-4" /> {p.name}
      </Link>

      <div className="mt-10 grid items-start gap-12 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="border border-line bg-white p-8 lg:col-span-6"
        >
          {m.img ? (
            <img
              src={m.img}
              alt={m.name}
              data-testid="model-detail-image"
              className="mx-auto aspect-square w-full max-w-md object-contain"
            />
          ) : (
            <div
              data-testid="model-detail-image"
              className="mx-auto flex aspect-square w-full max-w-md flex-col items-center justify-center gap-3 bg-moss text-sage"
            >
              <Droplets className="h-14 w-14" strokeWidth={1} />
              <span className="text-xs uppercase tracking-[0.2em]">Photo jald aa rahi hai</span>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease }}
          className="lg:col-span-6"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-sage">{p.name}</p>
          <h1
            data-testid="model-detail-name"
            className="mt-3 font-display text-4xl font-medium tracking-tight text-cream sm:text-5xl"
          >
            {m.name}
          </h1>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-sage sm:text-base">
            {p.desc}
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {(p.features || []).filter(Boolean).slice(0, 4).map((f) => (
              <div key={f} className="flex items-start gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-harvest" />
                <span className="text-sm leading-relaxed text-cream/80">{f}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {specs.filter(Boolean).map((sp) => (
              <span
                key={sp}
                data-testid="model-spec-chip"
                className="border border-line bg-moss px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-cream"
              >
                {sp}
              </span>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="model-enquire-button"
              className="group inline-flex items-center gap-3 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-harvestHover"
            >
              {t("enquireFor")} {m.name}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/catalogue/${p.id}`}
              data-testid="model-catalogue-button"
              className="group inline-flex items-center gap-3 border border-line px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-harvest hover:text-harvest"
            >
              {t("downloadCatalogue")}
              <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </div>

          <div className="mt-10 flex items-center gap-4 text-xs uppercase tracking-[0.15em]">
            {prev && (
              <Link
                to={`/products/${p.id}/models/${slugify(prev.name)}`}
                data-testid="model-prev-link"
                className="border border-line px-4 py-2 text-sage transition-colors duration-300 hover:border-harvest hover:text-harvest"
              >
                ← {prev.name}
              </Link>
            )}
            {next && (
              <Link
                to={`/products/${p.id}/models/${slugify(next.name)}`}
                data-testid="model-next-link"
                className="border border-line px-4 py-2 text-sage transition-colors duration-300 hover:border-harvest hover:text-harvest"
              >
                {next.name} →
              </Link>
            )}
          </div>
        </motion.div>
      </div>

      {others.length > 0 && (
        <div className="mt-20 border-t border-line pt-14" data-testid="model-others">
          <p className="text-xs uppercase tracking-[0.3em] text-harvest">
            {p.name} — {t("moreModels")}
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {others.map((o, i) => (
              <Link
                key={`${o.name}-${i}`}
                to={`/products/${p.id}/models/${slugify(o.name)}`}
                data-testid={`model-other-${i}`}
                className="group border border-line bg-white p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:border-harvest"
              >
                {o.img ? (
                  <img
                    src={o.img}
                    alt={o.name}
                    loading="lazy"
                    className="mx-auto h-28 w-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <span className="mx-auto flex h-28 w-full items-center justify-center bg-moss text-sage">
                    <Droplets className="h-7 w-7" strokeWidth={1.25} />
                  </span>
                )}
                <p className="mt-3 text-[11px] font-semibold uppercase leading-snug tracking-[0.1em] text-cream">
                  {o.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
